/**
 * Product Customizer Modal
 *
 * Renders the third-party customizer in a full screen iframe layered below the
 * popup / drawer stacking layer (base z-index: 999). The page is scroll locked
 * while it is open.
 *
 * The overlay has no close control of its own — that lives inside the iframe,
 * which is expected to postMessage out when it wants to be dismissed. Escape and
 * window.ProductCustomizer.close() also close it.
 *
 * Open via:
 *   - click on [data-product-customizer-trigger]
 *   - window.dispatchEvent(new CustomEvent('product-customizer:open'))
 *   - window.ProductCustomizer.open()
 *   - loading the page with the ?customizer=open param
 *
 * Iframe URL:
 *   Only the configurator's base URL is rendered by Liquid. The rest is assembled
 *   at open time by _buildFrameSrc as <base>/<shank>?metal=<code>, because the
 *   shank and the metal follow the selected variant, which is not known when the
 *   page is rendered.
 *
 * URL params:
 *   customizer=open   Written while the overlay is open and removed when it
 *                     closes, so the open state survives a reload or a shared
 *                     link. Present on load, it reopens the customizer. Opening
 *                     pushes a history entry carrying it, so the browser's Back
 *                     button closes the overlay and Forward reopens it.
 *   customizer-config The configurator's recreateUrl, captured from its
 *                     "config-updated" messages. Present on load, it replaces
 *                     the default iframe src so the shopper's design is
 *                     restored. It outlives a close so reopening resumes the
 *                     same design, and is only honoured when it points at the
 *                     configured customizer origin. It carries the whole design,
 *                     so it is loaded as is. See _frameSrc.
 *
 * Wishlist:
 *   A "ring-configurator:add-to-wishlist" message saves the design to the Swym
 *   wishlist as a custom item, via addCustomProductToList — an API Swym has to
 *   enable per store. See https://developers.getswym.com/docs/add-custom-item-to-list
 *   The item's du is the page URL including the customizer params, so opening it
 *   from the wishlist reopens that design. title, price and sku come from the
 *   rendered product, and the image from the Ring Customizer Default Image theme
 *   setting (falling back to the product image); the message payload can override
 *   any of them, and the price is taken from config.totalPrice first. Result is
 *   reported on a "product-customizer:wishlist" CustomEvent, and shown to the
 *   shopper on the theme's toast — the data API fires no Swym toast of its own.
 *
 * Drop a hint:
 *   A "ring-configurator:drop-a-hint" message opens the theme's Drop a hint dialog
 *   through the "drop-a-hint:open" document event, handing it the page URL so the
 *   hint points at the configured design instead of the plain product page.
 *
 * Book an appointment:
 *   A "ring-configurator:book-appointment" message opens the theme's booking modal
 *   with the engagement ring service preselected, and hands it the page URL as a
 *   "Your chosen pieces" booking property so the consultant can open the shopper's
 *   design before the appointment.
 *
 * Back:
 *   A "ring-configurator:back" message leaves the ring builder for the product
 *   page: the overlay closes, both customizer params are dropped so the URL is a
 *   plain product URL again, and the design is abandoned.
 *
 * Tooltips:
 *   Every time the iframe finishes loading, a "ring-configurator:tooltips" message
 *   is posted into it listing the filter tooltips rendered in the layout, so the
 *   configurator knows which info icons will open something. Payload:
 *     { type, ids: ['filter-tooltip-<handle>', ...], tooltips: [{ id, handle, key }] }
 *   It is only sent to the configured customizer origin.
 *
 * Messages from the iframe:
 *   Only accepted from the origin of the configured iframe src, and only from
 *   the iframe's own contentWindow. A payload of "close" — as a bare string, or
 *   as the type / action / event / message key of an object — closes the
 *   overlay. Everything else is re-dispatched on the element as a
 *   "product-customizer:message" CustomEvent so later work (add to cart, price
 *   sync) can listen without editing this file. Add data-debug to the element to
 *   log every accepted message while developing the hand-off.
 *
 * IMPLEMENTATION NOTE: this is a classic script sharing the global lexical scope
 * with the theme's other custom assets, so everything is kept inside an IIFE.
 */

(() => {
  const OPEN_EVENT = 'product-customizer:open';
  const CLOSE_EVENT = 'product-customizer:close';
  const MESSAGE_EVENT = 'product-customizer:message';
  const WISHLIST_EVENT = 'product-customizer:wishlist';
  const ELEMENT_NAME = 'product-customizer-modal';

  const OPEN_PARAM = 'customizer';
  const OPEN_PARAM_VALUE = 'open';
  const CONFIG_PARAM = 'customizer-config';

  // Marks the history entry pushed by open(), so close() knows it can step back off
  // it rather than leave a duplicate product page entry behind.
  const HISTORY_STATE_KEY = 'productCustomizer';

  // Payloads that mean "dismiss the overlay". Kept liberal because the iframe
  // side of the contract is not built yet.
  const CLOSE_MESSAGES = ['close', 'closed', 'dismiss', 'exit', 'customizer:close'];

  // Sent by the configurator when a shopper taps one of its info icons. The title
  // names the filter tooltip to open.
  const INFO_ICON_MESSAGE = 'ring-configurator:info-icon';

  // Sent by the configurator whenever the design changes, carrying a recreateUrl
  // that reopens that design.
  const CONFIG_UPDATED_MESSAGE = 'ring-configurator:config-updated';

  // Sent by the configurator when the shopper saves the design to their wishlist.
  const ADD_TO_WISHLIST_MESSAGE = 'ring-configurator:add-to-wishlist';

  // Sent by the configurator when the shopper wants to hint at the design.
  const DROP_A_HINT_MESSAGE = 'ring-configurator:drop-a-hint';

  // Sent by the configurator's back control, which leaves the builder for the
  // product page rather than closing over the design like a dismiss does.
  const BACK_MESSAGE = 'ring-configurator:back';

  // Posted into the configurator on every iframe load, listing the filter tooltips
  // its info icons can open.
  const TOOLTIPS_MESSAGE = 'ring-configurator:tooltips';

  // Sent by the configurator when the shopper wants to see the design in person.
  const BOOK_APPOINTMENT_MESSAGE = 'ring-configurator:book-appointment';

  // The service the booking modal preselects for a configured ring. Matched against
  // the service product's type, then its title — see _findServiceByType in
  // assets/book-appointment.js.
  const APPOINTMENT_SERVICE_TYPE = 'Engagement Rings';

  // Marks wishlist entries created from the ring builder, alongside the page URL
  // that reopens the saved design.
  const WISHLIST_PROPS = { ring_builder: true };

  // Name given to the wishlist created for a shopper who has none yet. Matches
  // Swym's own default so it reads the same as a list Swym would have created.
  const DEFAULT_LIST_NAME = 'My Wishlist';

  // Swym only raises its own "added to wishlist" toast from its button flow. The
  // ring builder saves through the addCustomProductToList data API instead, which
  // has no UI layer of its own, so the customizer raises the theme's toast here.
  // The toast element lives in layout/theme.liquid and sits above the overlay.
  //
  // The copy is a translation, read off window.themeVariables.strings at toast time
  // — see product.ring_customizer in locales/ and snippets/js-variables.liquid. The
  // English defaults below are only a backstop for a storefront whose locale file
  // predates these keys, so a missing translation never toasts "undefined".
  const TOAST_EVENT = 'toast:show';
  const WISHLIST_TOAST_SUCCESS = 'Your configuration has been added to your wishlist';
  const WISHLIST_TOAST_ERROR = 'Your design could not be saved to your wishlist. Please try again.';
  const WISHLIST_TOAST_PRODUCT_SUCCESS = 'This item has been added to your wishlist';

  // The configurator's design details are flattened into cprops under this prefix,
  // which keeps them readable in Swym and out of the way of the props above.
  const CONFIG_PROP_PREFIX = 'config_';
  const CONFIG_MAX_DEPTH = 3;
  const CONFIG_MAX_PROPS = 40;
  const CONFIG_MAX_VALUE_LENGTH = 500;

  // Safari throws once history.replaceState is called more than 100 times in 30
  // seconds, and config updates can arrive on every drag, so writes are debounced.
  const CONFIG_WRITE_DELAY = 400;

  // The theme reference counts its own dialog scroll lock through the .lock class,
  // so closing a filter tooltip over the customizer would clear it and let the page
  // scroll behind the overlay. The overlay therefore locks with a class of its own.
  const LOCK_CLASS = 'product-customizer-lock';

  // The configurator names metals by code while the store's metal colour option
  // spells them out, so the option value is translated on the way into the URL.
  // Keyed on the normalised option value, see _metalValue.
  const METAL_CODES = {
    platinum: 'platinum',
    yellow_gold: '10kt_yellow_gold',
    white_gold: '10kt_white_gold',
    rose_gold: '10kt_rose_gold',
  };

  // Fallback for option values that carry a karat or a qualifier, so "14K White Gold"
  // resolves the same as "White Gold". Order matters: the first keyword found wins.
  const METAL_KEYWORDS = [
    ['platinum', 'pt'],
    ['rose', 'e'],
    ['yellow', 'y'],
    ['white', 'w'],
  ];

  class ProductCustomizerModal extends HTMLElement {
    _lockOwned = false;
    _abortController = null;
    _triggerEl = null;
    _frameOrigin = null;
    _configUrl = null;
    _configWriteTimer = null;
    _listId = null;
    _pendingConfigUrl = null;
    _shankMap = {};
    _frameKey = null;

    connectedCallback() {
      // The overlay is position: fixed and must never inherit a containing block
      // from the product info, so it lives on the body. Moving the node re-enters
      // this callback, which is where the real setup happens.
      if (this.parentElement !== document.body) {
        document.body.append(this);
        return;
      }

      // Drop any copy left on the body by an earlier render of the section.
      document.querySelectorAll(`body > ${this.localName}`).forEach((modal) => {
        if (modal !== this) modal.remove();
      });

      this.dialog = this.querySelector('[data-product-customizer-dialog]');
      this.frame = this.querySelector('[data-product-customizer-frame]');
      this._frameOrigin = this._resolveFrameOrigin();
      this._shankMap = this._readShankMap();

      this._bindUi();
      this._restoreFromUrl();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._abortController = null;
      clearTimeout(this._configWriteTimer);
      document.removeEventListener('keydown', this._onKeydown);
      this._unlockScroll();
    }

    _resolveFrameOrigin() {
      const src = this.dataset.productCustomizerSrc;
      if (!src) return null;

      try {
        return new URL(src, window.location.href).origin;
      } catch {
        // A malformed src leaves the origin null, which rejects every message.
        return null;
      }
    }

    /*
     * The shank of every variant is rendered alongside the overlay, because the
     * variant:change payload the theme bubbles up is the variant JSON and carries
     * no metafields.
     */
    _readShankMap() {
      const script = this.querySelector('[data-product-customizer-shanks]');
      if (!script) return {};

      try {
        return JSON.parse(script.textContent) || {};
      } catch (error) {
        this._debug('could not read the shank map', error);
        return {};
      }
    }

    /*
     * Builds the configurator URL from the rendered base: the shank of the selected
     * variant is appended as a path segment and its metal added as a query param, so
     * neither can be baked in at render time.
     */
    _buildFrameSrc() {
      const base = this.dataset.productCustomizerSrc;
      if (!base) return '';

      try {
        const url = new URL(base, window.location.href);

        if (this.dataset.shank) {
          if (!url.pathname.endsWith('/')) url.pathname += '/';
          url.pathname += encodeURIComponent(this.dataset.shank);
        }

        const metal = this._metalValue(this.dataset.metalOption);
        if (metal) url.searchParams.set('metal', metal);

        return url.href;
      } catch (error) {
        this._debug('could not build the customizer url', error);
        return '';
      }
    }

    /*
     * Translates a metal colour option value into the code the configurator uses. An
     * unrecognised value resolves to null and the metal is left off the URL, so the
     * configurator falls back to its own default rather than loading a wrong metal.
     *
     * Values are normalised to lower snake case first, so "White Gold", "white-gold"
     * and "white_gold" all reach the same key.
     */
    _metalValue(value) {
      if (typeof value !== 'string') return null;

      const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
      if (!normalized) return null;

      if (METAL_CODES[normalized]) return METAL_CODES[normalized];

      const keyword = METAL_KEYWORDS.find(([match]) => normalized.includes(match));
      if (keyword) return keyword[1];

      this._debug('unrecognised metal option', value);
      return null;
    }

    /*
     * The iframe is only loaded the first time the customizer is opened, and is kept
     * afterwards so the shopper does not lose their design on close.
     *
     * A change of shank or metal is the exception: the shopper has picked a different
     * ring on the product page, so the design built on the old one no longer applies
     * and is dropped along with the config param that would otherwise restore it.
     */
    _loadFrame() {
      if (!this.frame) return;

      const key = `${this.dataset.shank || ''}|${this.dataset.metalOption || ''}`;
      const loaded = this.frame.getAttribute('src');

      if (loaded && key === this._frameKey) return;

      if (loaded) {
        this._debug('variant changed, starting a new design', { from: this._frameKey, to: key });
        clearTimeout(this._configWriteTimer);
        this._configWriteTimer = null;
        this._configUrl = null;
        this._pendingConfigUrl = null;
        this._writeUrlParams({ [CONFIG_PARAM]: null });
      }

      this._frameKey = key;

      const src = this._frameSrc();
      this._debug('customizer url', src);

      this._swapFrame(src);
    }

    /*
     * Pointing an iframe that has already loaded at a new src adds an entry to the
     * joint session history, so the browser's Back button would step the iframe back
     * (to about:blank, or the previous design) while the address bar stays put and
     * the shopper is left looking at an empty overlay. A fresh element has no history
     * of its own, so the frame is replaced rather than renavigated. Pass no src to
     * leave an empty frame.
     */
    _swapFrame(src) {
      if (!this.frame) return;

      const fresh = this.frame.cloneNode(false);
      fresh.removeAttribute('src');
      if (src) fresh.setAttribute('src', src);
      fresh.addEventListener('load', this._onFrameLoad, { signal: this._abortController?.signal });

      this.frame.replaceWith(fresh);
      this.frame = fresh;
    }

    /*
     * The src to load: the default URL for a fresh design, or the restored design's
     * recreateUrl. A recreateUrl is the whole design — every modification the shopper
     * made inside the configurator, not just the shank and the metal the product page
     * knows about — so it is loaded untouched.
     */
    _frameSrc() {
      return this._configUrl || this._buildFrameSrc();
    }

    /*
     * Reads the params written by a previous session and reopens from them. A
     * config URL is adopted before the open so the iframe boots straight into the
     * saved design rather than the default one.
     */
    _restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const storedConfig = params.get(CONFIG_PARAM);

      if (storedConfig) {
        this._configUrl = this._sanitizeConfigUrl(storedConfig);

        if (this._configUrl) {
          this._debug('restored config url from the address bar', this._configUrl);
        } else {
          // Do not leave a rejected value sitting in the address bar to be shared on.
          this._debug('discarded an untrusted config url from the address bar', storedConfig);
          this._writeUrlParams({ [CONFIG_PARAM]: null });
        }
      }

      if (params.get(OPEN_PARAM) === OPEN_PARAM_VALUE) {
        this._debug('reopening from the address bar');
        this.open();
      }
    }

    /*
     * The config URL ends up as the src of a full screen iframe on a storefront
     * page, so it is pinned to the configured customizer origin. Without this a
     * crafted link would frame an attacker's page on the shop's own domain.
     */
    _sanitizeConfigUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return null;

      try {
        const url = new URL(value, window.location.href);
        return this._frameOrigin && url.origin === this._frameOrigin ? url.href : null;
      } catch {
        return null;
      }
    }

    _writeUrlParams(updates) {
      try {
        const url = new URL(window.location.href);

        Object.entries(updates).forEach(([key, value]) => {
          if (value === null) {
            url.searchParams.delete(key);
          } else {
            url.searchParams.set(key, value);
          }
        });

        if (url.href !== window.location.href) {
          window.history.replaceState(window.history.state, '', url);
        }
      } catch (error) {
        // A throttled or unavailable History API must never break the overlay.
        this._debug('could not update the address bar', error);
      }
    }

    _bindUi() {
      // Listeners are tied to a controller so a node dropped by a later render of
      // the section stops responding to triggers.
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      document.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest('[data-product-customizer-trigger]');
          if (!trigger) return;

          event.preventDefault();
          this.open({ trigger });
        },
        { signal }
      );

      window.addEventListener(OPEN_EVENT, () => this.open(), { signal });
      window.addEventListener(CLOSE_EVENT, () => this.close(), { signal });
      window.addEventListener('message', this._onMessage, { signal });
      window.addEventListener('popstate', this._onPopState, { signal });
      this.frame?.addEventListener('load', this._onFrameLoad, { signal });

      // The block is not partially re-rendered on variant change, so the variant is
      // tracked off the event the theme bubbles up from the product form.
      document.addEventListener('variant:change', this._onVariantChange, { signal });
    }

    /*
     * Hands the configurator the list of filter tooltips present on the page. Fires
     * on every load, including the fresh frame swapped in after a variant change.
     * The empty frame left by _back has no src and is skipped.
     */
    _onFrameLoad = () => {
      if (!this._frameOrigin || !this.frame?.getAttribute('src')) return;

      const tooltips = Array.from(document.querySelectorAll('.filter-tooltip[id^="filter-tooltip-"]')).map((tooltip) => ({
        id: tooltip.id,
        handle: tooltip.id.replace('filter-tooltip-', ''),
        key: tooltip.dataset.filterTooltipKey || null,
      }));

      const message = { type: TOOLTIPS_MESSAGE, ids: tooltips.map(({ id }) => id), tooltips };

      this._debug('sending tooltips', message);
      this.frame.contentWindow?.postMessage(message, this._frameOrigin);
    };

    _onMessage = (event) => {
      // Any page can postMessage into this window, so the sender is pinned to the
      // configured origin and to the iframe itself before the payload is trusted.
      if (!this._frameOrigin || event.origin !== this._frameOrigin) return;
      if (!this.frame || event.source !== this.frame.contentWindow) return;

      this._debug('message', event.data);

      const name = this._messageName(event.data);

      this.dispatchEvent(
        new CustomEvent(MESSAGE_EVENT, { detail: event.data, bubbles: true })
      );

      if (name === INFO_ICON_MESSAGE) {
        this._openFilterTooltip(event.data?.title);
        return;
      }

      if (name === CONFIG_UPDATED_MESSAGE) {
        this._storeConfigUrl(event.data?.recreateUrl);
        return;
      }

      // WISHLIST-TOGGLE: ring configurator "add to wishlist" handler. To disable, comment out this block.
      if (name === ADD_TO_WISHLIST_MESSAGE) {
        this._addToWishlist(event.data);
        return;
      }

      if (name === DROP_A_HINT_MESSAGE) {
        this._openDropAHint();
        return;
      }

      if (name === BOOK_APPOINTMENT_MESSAGE) {
        this._openBookAppointment();
        return;
      }

      if (name === BACK_MESSAGE) {
        this._back();
        return;
      }

      if (CLOSE_MESSAGES.includes(name)) {
        this.close();
      }
    };

    /*
     * Records the recreateUrl of the current design in the address bar so a reload
     * or a shared link comes back to it. The iframe is deliberately not reloaded —
     * it already shows this design.
     */
    _storeConfigUrl(recreateUrl) {
      const configUrl = this._sanitizeConfigUrl(recreateUrl);

      if (!configUrl) {
        this._debug('ignored an unusable recreateUrl', recreateUrl);
        return;
      }

      if (configUrl === this._configUrl) return;

      this._configUrl = configUrl;
      this._pendingConfigUrl = configUrl;

      clearTimeout(this._configWriteTimer);
      this._configWriteTimer = setTimeout(() => this._flushConfigWrite(), CONFIG_WRITE_DELAY);
    }

    /*
     * Writes any debounced config URL straight away. Called before anything that
     * captures the page URL, so the captured URL reflects the current design.
     */
    _flushConfigWrite() {
      clearTimeout(this._configWriteTimer);
      this._configWriteTimer = null;

      if (!this._pendingConfigUrl) return;

      const configUrl = this._pendingConfigUrl;
      this._pendingConfigUrl = null;

      this._debug('writing config url', configUrl);
      this._writeUrlParams({ [CONFIG_PARAM]: configUrl });
    }

    /*
     * The block is not partially re-rendered on variant change, so the rendered
     * product details would go stale as soon as the shopper switches metal. They are
     * refreshed here from the event the theme bubbles up off the product form, and
     * feed the wishlist item's title, price and image.
     */
    _onVariantChange = (event) => {
      const formId = this.dataset.formId;
      const variant = event.detail?.variant;

      // Ignore other product forms on the page (featured product, quick buy). Matched
      // on the form element rather than detail.formId, because a form's .id resolves
      // to its "id" named input — detail.formId is that element, never the string it
      // looks like, so comparing it to a string rejected every event.
      if (formId && event.target?.getAttribute?.('id') !== formId) return;
      if (!variant?.id) return;

      this.dataset.variantId = String(variant.id);

      if (variant.price != null) this.dataset.productPrice = String(variant.price);
      if (variant.sku) this.dataset.productSku = String(variant.sku);
      if (variant.featured_image?.src) this.dataset.productImage = variant.featured_image.src;

      // The shank drives the configurator URL, so it follows the variant too. It is
      // applied to the iframe on the next open, see _loadFrame.
      const shank = this._shankMap?.[variant.id];
      if (shank == null || shank === '') {
        delete this.dataset.shank;
      } else {
        this.dataset.shank = String(shank);
      }

      // The metal colour option is read off the variant by position, because its name
      // varies by product. Applied to the iframe on the next open, see _loadFrame.
      const metalPosition = parseInt(this.dataset.metalOptionPosition, 10);

      if (metalPosition > 0) {
        const metalOption = variant.options?.[metalPosition - 1];

        if (metalOption) {
          this.dataset.metalOption = String(metalOption);
        } else {
          delete this.dataset.metalOption;
        }
      }

      this._debug('tracking variant', variant.id, {
        shank: this.dataset.shank,
        metal: this.dataset.metalOption,
      });
    };

    /*
     * Opens the theme's Book an appointment modal for the configured ring, with the
     * engagement ring service preselected. The page URL — which carries the customizer
     * params — rides along as a booking property, so the consultant opens the design
     * the shopper built rather than a bare product page.
     *
     * The booking modal is at z-index 10000 and this overlay at 998, so it stacks over
     * the customizer instead of replacing it, the same way Drop a hint does. The two
     * scroll locks are independent, so neither clears the other.
     */
    _openBookAppointment() {
      // The config write is debounced; flush it so the booked URL is the current design.
      this._flushConfigWrite();

      const detail = {
        productType: APPOINTMENT_SERVICE_TYPE,
        relatedProduct: window.location.href,
      };

      this._debug('opening book an appointment', detail);
      window.dispatchEvent(new CustomEvent('book-appointment:open', { detail }));
    }

    /*
     * Leaves the ring builder for the product page. Unlike a dismiss, which closes
     * over the design so reopening resumes it, back abandons it: the pending config
     * write is cancelled and both customizer params are removed, so what is left in
     * the address bar is a plain product URL.
     *
     * The iframe is unloaded too, otherwise reopening would show the design that was
     * just walked away from while the URL claimed a fresh build.
     */
    _back() {
      clearTimeout(this._configWriteTimer);
      this._configWriteTimer = null;
      this._configUrl = null;
      this._pendingConfigUrl = null;

      this.close();
      this._writeUrlParams({ [OPEN_PARAM]: null, [CONFIG_PARAM]: null });

      this._frameKey = null;

      // An empty replacement tears the configurator down without leaving an
      // about:blank history entry behind, and has no src so _loadFrame treats the
      // next open as a first load.
      this._swapFrame(null);

      this._debug('left the builder');
    }

    /*
     * Opens the theme's Drop a hint dialog for the configured ring rather than the
     * plain product page, by handing it the page URL — which carries the customizer
     * params, so the recipient lands on the design the shopper built.
     *
     * The dialog is a modal at z-index 999 and this overlay sits at 998, so the hint
     * form stacks above the customizer instead of replacing it. The hint form's own
     * id is built from the section and block ids, so it is reached through the same
     * document event convention the filter tooltips use.
     */
    _openDropAHint() {
      // The config write is debounced; flush it so the hinted URL is the current design.
      this._flushConfigWrite();

      const productUrl = window.location.href;

      // A configured ring has no product photo, so the theme's customizer image is
      // sent instead when the merchant has set one.
      const productImage = window.themeVariables?.settings?.ringCustomizerDefaultImage || '';

      this._debug('opening drop a hint', { productUrl, productImage });
      document.dispatchEvent(
        new CustomEvent('drop-a-hint:open', { detail: { productUrl, productImage } })
      );
    }

    /*
     * Adds the configured ring to the Swym wishlist as a CUSTOM item, so the entry
     * describes the shopper's design rather than the bare shank product.
     * See https://developers.getswym.com/docs/add-custom-item-to-list
     *
     * du carries the page URL including the customizer params, so opening the entry
     * from the wishlist reopens that exact design. du / title / price are mandatory;
     * the configurator may override any of them through the message payload, since
     * it knows the real configured title and price.
     */
    _addToWishlist(data) {
      // Outside the open builder there is no design to describe, so the product is
      // saved as a standard Swym item, the same as any other wishlist button.
      if (!this.hasAttribute('open')) {
        this._addProductToWishlist();
        return;
      }

      // The config write is debounced, so flush it before capturing the URL —
      // otherwise the wishlist entry points at the design as it was a moment ago.
      this._flushConfigWrite();

      const du = window.location.href;
      const title = this._firstString(data?.title, this.dataset.productTitle);
      const price = this._wishlistPrice(this._configTotalPrice(data?.config), data?.price);

      if (!title || price == null) {
        console.error('[product-customizer] wishlist add needs a title and a price', { title, price });
        this._emitWishlistResult(false, null, 'missing title or price');
        return;
      }

      const customItem = {
        du,
        title,
        price,
        qty: 1,
      };

      console.log({
        customItem
      })

      const sku = this._firstString(data?.sku, this.dataset.productSku);
      // An image sent by the configurator is a render of the design itself, so it
      // wins over the shank's product photo.
      const image = this._firstString(data?.image, data?.iu, this.dataset.productImage);

      if (sku) customItem.sku = sku;
      if (image) customItem.iu = image;

      // IMPLEMENTATION NOTE: cprops is not part of the documented custom item schema.
      // It is sent because the ring builder marker was asked for, and is harmless if
      // Swym drops it — but do not rely on it without checking a real list entry.
      //
      // Config details go first so the prefixed keys can never displace the three
      // props the theme itself depends on.
      customItem.cprops = {
        ...this._configProps(data?.config),
        ...WISHLIST_PROPS,
        url: du,
        timestamp: new Date().toISOString(),
      };

      this._debug('adding custom item to the wishlist', customItem);

      this._withSwym((swat) => this._sendToWishlist(swat, customItem));
    }

    /*
     * Adds the selected variant as a standard Swym item — no ring builder marker, no
     * config props, and a plain product URL — so it lists like any other product.
     * See https://developers.getswym.com/docs/add-to-list
     */
    _addProductToWishlist() {
      const epi = Number(this.dataset.variantId);
      const empi = Number(this.dataset.productId);
      const du = this._firstString(this.dataset.productUrl);

      if (!epi || !empi || !du) {
        console.error('[product-customizer] wishlist add needs a product, variant and url', { epi, empi, du });
        this._emitWishlistResult(false, null, 'missing product details', { custom: false });
        return;
      }

      const product = { epi, empi, du };

      this._debug('adding product to the wishlist', product);

      this._withSwym((swat) => {
        const onDone = (ok) => (payload) => {
          if (!ok) {
            // A stale list id is the likeliest failure, so drop it and refetch next time.
            this._listId = null;
            console.error('[product-customizer] wishlist add failed', payload);
          }

          this._emitWishlistResult(ok, product, payload, { custom: false });
        };

        if (typeof swat?.addToList !== 'function') {
          onDone(false)('addToList unavailable');
          return;
        }

        this._resolveListId(swat, (lid) => {
          if (!lid) {
            onDone(false)('no wishlist could be resolved');
            return;
          }

          try {
            swat.addToList(lid, product, onDone(true), onDone(false));
          } catch (error) {
            onDone(false)(error);
          }
        });
      });
    }

    /*
     * Flattens the configurator's design details into cprops entries.
     *
     * Swym renders cprops as key/value rows, so a nested config is walked into dotted
     * keys ("config_centerStone.shape") rather than dumped as one JSON blob — a sales
     * team reading a saved design wants readable rows. Scalar arrays are joined,
     * anything past the depth limit is stringified so no detail is silently lost, and
     * the size caps keep a runaway config from bloating the request.
     */
    _configProps(config) {
      if (config === null || config === undefined || config === '') return {};

      let source = config;

      // The config may arrive already serialised.
      if (typeof source === 'string') {
        try {
          source = JSON.parse(source);
        } catch {
          return { [`${CONFIG_PROP_PREFIX}details`]: this._truncate(source) };
        }
      }

      if (typeof source !== 'object') {
        return { [`${CONFIG_PROP_PREFIX}details`]: this._truncate(String(source)) };
      }

      const props = {};
      let dropped = 0;

      const isScalar = (value) =>
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

      const add = (path, value) => {
        if (!path) return;

        if (Object.keys(props).length >= CONFIG_MAX_PROPS) {
          dropped += 1;
          return;
        }

        props[CONFIG_PROP_PREFIX + path] = this._truncate(String(value));
      };

      const walk = (value, path, depth) => {
        if (value === null || value === undefined || value === '') return;

        if (isScalar(value)) {
          add(path, value);
          return;
        }

        if (Array.isArray(value)) {
          if (value.every(isScalar)) {
            add(path, value.join(', '));
          } else if (depth >= CONFIG_MAX_DEPTH) {
            add(path, this._safeJson(value));
          } else {
            value.forEach((entry, index) => walk(entry, `${path}.${index}`, depth + 1));
          }
          return;
        }

        if (typeof value === 'object') {
          if (depth >= CONFIG_MAX_DEPTH) {
            add(path, this._safeJson(value));
            return;
          }

          Object.entries(value).forEach(([key, entry]) =>
            walk(entry, path ? `${path}.${key}` : key, depth + 1)
          );
          return;
        }

        add(path, this._safeJson(value));
      };

      walk(source, '', 0);

      if (dropped) {
        this._debug(`config had more than ${CONFIG_MAX_PROPS} properties, ${dropped} dropped`);
      }

      return props;
    }

    _truncate(text) {
      return text.length > CONFIG_MAX_VALUE_LENGTH
        ? `${text.slice(0, CONFIG_MAX_VALUE_LENGTH)}…`
        : text;
    }

    _safeJson(value) {
      try {
        return JSON.stringify(value);
      } catch {
        // Circular structures are not worth failing the whole wishlist add over.
        return String(value);
      }
    }

    _sendToWishlist(swat, customItem) {
      const onDone = (ok) => (payload) => {
        if (ok) {
          this._debug('added custom item to the wishlist', payload);
        } else {
          // A stale list id is the likeliest failure, so drop it and refetch next time.
          this._listId = null;
          console.error('[product-customizer] wishlist add failed', payload);
        }

        this._emitWishlistResult(ok, customItem, payload);
      };

      if (typeof swat?.addCustomProductToList !== 'function') {
        console.error(
          '[product-customizer] Swym addCustomProductToList is unavailable. The custom ' +
            'item API has to be enabled for this store by Swym support.'
        );
        onDone(false)('addCustomProductToList unavailable');
        return;
      }

      this._resolveListId(swat, (lid) => {
        if (!lid) {
          onDone(false)('no wishlist could be resolved');
          return;
        }

        this._debug('adding custom item to list', lid);

        try {
          swat.addCustomProductToList(lid, customItem, onDone(true), onDone(false));
        } catch (error) {
          onDone(false)(error);
        }
      });
    }

    /*
     * Resolves the list to add to, mirroring how the wishlist page reads lists.
     * Swym only creates the default list on the first standard add, so a shopper who
     * has never saved anything (typically a guest) has no list yet. One is created
     * for them in that case.
     */
    _resolveListId(swat, callback) {
      if (this._listId) {
        callback(this._listId);
        return;
      }

      this._fetchFirstListId(swat, (lid) => {
        if (lid) {
          callback(lid);
          return;
        }

        this._createDefaultList(swat, callback);
      });
    }

    _fetchFirstListId(swat, callback) {
      if (typeof swat?.fetchLists !== 'function') {
        callback(null);
        return;
      }

      swat.fetchLists({
        callbackFn: (lists) => {
          const normalized = Array.isArray(lists)
            ? lists
            : Array.isArray(lists?.lists)
              ? lists.lists
              : lists && typeof lists === 'object' && lists.lid
                ? [lists]
                : [];

          this._listId = normalized[0]?.lid || null;
          this._debug('resolved wishlist id', this._listId);
          callback(this._listId);
        },
        errorFn: (error) => {
          this._debug('fetchLists failed', error);
          callback(null);
        },
      });
    }

    _createDefaultList(swat, callback) {
      if (typeof swat?.createList !== 'function') {
        callback(null);
        return;
      }

      this._debug('no wishlist found, creating', DEFAULT_LIST_NAME);

      try {
        swat.createList(
          { lname: DEFAULT_LIST_NAME },
          (list) => {
            this._listId = list?.lid || null;
            this._debug('created wishlist', this._listId);
            callback(this._listId);
          },
          (error) => {
            // List names are unique per shopper, so this usually means the list was
            // created elsewhere in the meantime (another tab, a Swym button). Refetch.
            this._debug('createList failed, refetching', error);
            this._fetchFirstListId(swat, callback);
          }
        );
      } catch (error) {
        this._debug('createList threw', error);
        callback(null);
      }
    }

    _firstString(...values) {
      const found = values.find((value) => typeof value === 'string' && value.trim());
      return found ? found.trim() : null;
    }

    /*
     * Swym takes the price in major units (dollars), but the theme renders it in
     * cents — both the Liquid variant.price and the variant change event are cents.
     * Prices sent by the configurator (config.totalPrice, then price) are taken as
     * dollars and passed through.
     */
    _wishlistPrice(...configuredPrices) {
      const price = this._firstNumber(...configuredPrices);
      if (price != null) return price;

      const cents = this._firstNumber(this.dataset.productPrice);
      return cents == null ? null : cents / 100;
    }

    /* The configured design's total, in dollars. The config may arrive serialised. */
    _configTotalPrice(config) {
      let source = config

      if (typeof source === 'string') {
        try {
          source = JSON.parse(source);
        } catch {
          return null;
        }
      }

      return source && typeof source === 'object' ? source.totalPrice : null;
    }

    _firstNumber(...values) {
      for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const number = Number(value);
        if (Number.isFinite(number)) return number;
      }

      return null;
    }

    _emitWishlistResult(ok, product, detail, { custom = true } = {}) {
      this._toastWishlistResult(ok, custom);

      this.dispatchEvent(
        new CustomEvent(WISHLIST_EVENT, {
          detail: { ok, product, detail, custom },
          bubbles: true,
        })
      );
    }

    /*
     * Tells the shopper the save landed, standing in for the Swym toast the data
     * API never fires. Every add path funnels through _emitWishlistResult, so the
     * failures that never reach Swym are covered alongside the ones it rejects.
     *
     * The copy names the configuration rather than the product, because what was
     * saved is the shopper's design and not the shank it was built on.
     */
    _toastWishlistResult(ok, custom = true) {
      const strings = window.themeVariables?.strings;

      const success = custom
        ? strings?.ringCustomizerAddedToWishlist || WISHLIST_TOAST_SUCCESS
        : strings?.ringCustomizerAddedProductToWishlist || WISHLIST_TOAST_PRODUCT_SUCCESS;

      const message = ok ? success : strings?.ringCustomizerWishlistError || WISHLIST_TOAST_ERROR;

      document.dispatchEvent(
        new CustomEvent(TOAST_EVENT, {
          detail: ok ? { message } : { message, tone: 'error' },
        })
      );
    }

    /*
     * Matches the wishlist page's approach: the SDK may already be ready by the time
     * this deferred script runs, otherwise queue behind SwymCallbacks.
     */
    _withSwym(callback) {
      if (window._swat) {
        callback(window._swat);
        return;
      }

      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(callback);
    }

    /*
     * Opens the filter tooltip matching an info icon the shopper tapped inside the
     * configurator. Filter tooltips are rendered once in the layout and stack above
     * this overlay (they are modals at z-index 999, the overlay sits at 998), so the
     * customizer stays open behind the popup.
     *
     * The metaobject "key" field exists to be matched against a filter label, which
     * is exactly what the configurator sends, so it is tried first. The metaobject
     * handle is a fallback for tooltips whose key was never filled in.
     */
    _openFilterTooltip(title) {
      const label = typeof title === 'string' ? title.trim() : '';

      if (!label) {
        this._debug('info icon message carried no usable title', title);
        return;
      }

      const key = label.toLowerCase();
      const handle = key
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      let detail = null;

      if (document.querySelector(`[data-filter-tooltip-key="${CSS.escape(key)}"]`)) {
        detail = { key };
      } else if (handle && document.getElementById(`filter-tooltip-${handle}`)) {
        detail = { handle };
      }

      if (!detail) {
        this._debug(
          `no filter tooltip matches "${label}" (tried key "${key}" and handle "${handle}")`,
          Array.from(document.querySelectorAll('.filter-tooltip')).map((tooltip) => ({
            handle: tooltip.id.replace('filter-tooltip-', ''),
            key: tooltip.dataset.filterTooltipKey,
          }))
        );
        return;
      }

      this._debug('opening filter tooltip', detail);
      document.dispatchEvent(new CustomEvent('filter-tooltip:open', { detail }));
    }

    _messageName(data) {
      const name =
        typeof data === 'string'
          ? data
          : data && typeof data === 'object'
            ? data.type || data.action || data.event || data.message
            : null;

      return typeof name === 'string' ? name.trim().toLowerCase() : null;
    }

    open({ trigger = null } = {}) {
      if (this.hasAttribute('open')) return;

      this._triggerEl = trigger || null;

      this.setAttribute('open', '');

      this._loadFrame();

      this._lockScroll();
      document.addEventListener('keydown', this._onKeydown);
      this.dialog?.focus({ preventScroll: true });

      this._triggerEl?.setAttribute('aria-expanded', 'true');
      this._pushOpenEntry();
    }

    /*
     * Opening adds a history entry so the browser's Back button closes the overlay
     * instead of leaving the product page, and Forward reopens it. No entry is added
     * when the URL already says the overlay is open — a shared link, a reload, or a
     * reopen driven by popstate — since Back from there should leave as it always has.
     */
    _pushOpenEntry() {
      const params = new URLSearchParams(window.location.search);
      if (params.get(OPEN_PARAM) === OPEN_PARAM_VALUE) return;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set(OPEN_PARAM, OPEN_PARAM_VALUE);

        const state = window.history.state;
        const nextState = state && typeof state === 'object' ? { ...state } : {};
        nextState[HISTORY_STATE_KEY] = true;

        window.history.pushState(nextState, '', url);
      } catch (error) {
        this._debug('could not add a history entry', error);
        this._writeUrlParams({ [OPEN_PARAM]: OPEN_PARAM_VALUE });
      }
    }

    _ownsHistoryEntry() {
      const state = window.history.state;
      return Boolean(state && typeof state === 'object' && state[HISTORY_STATE_KEY]);
    }

    /*
     * Closing from the overlay (the configurator's close or back, Escape, the
     * close event) steps off the entry open() pushed, so the history stack matches
     * what the browser's Back button would have left. Without that entry — the page
     * was loaded with the overlay open — the open flag is just dropped in place.
     */
    close() {
      if (!this.hasAttribute('open')) return;

      this._closeUi();

      if (this._ownsHistoryEntry()) {
        // Keep the entry Forward returns to on the current design.
        this._flushConfigWrite();
        window.history.back();
        return;
      }

      // Only the open flag is dropped. The config param outlives the close so
      // reopening resumes the same design.
      this._writeUrlParams({ [OPEN_PARAM]: null });
    }

    _closeUi() {
      this.removeAttribute('open');
      this._unlockScroll();
      document.removeEventListener('keydown', this._onKeydown);

      this._triggerEl?.setAttribute('aria-expanded', 'false');
      this._triggerEl?.focus();
      this._triggerEl = null;
    }

    /*
     * Follows the browser's Back and Forward: the overlay is open exactly when the
     * entry's URL says so. The entry stepped onto may predate the shopper's latest
     * design (or still name one _back abandoned), so its config param is brought in
     * line with the design held in memory, which is what reopening will show.
     */
    _onPopState = () => {
      const isOpen = new URLSearchParams(window.location.search).get(OPEN_PARAM) === OPEN_PARAM_VALUE;

      if (isOpen && !this.hasAttribute('open')) {
        this._debug('reopening from history');
        this.open();
      } else if (!isOpen && this.hasAttribute('open')) {
        this._debug('closing from history');
        this._closeUi();
      }

      clearTimeout(this._configWriteTimer);
      this._configWriteTimer = null;
      this._pendingConfigUrl = null;
      this._writeUrlParams({ [CONFIG_PARAM]: this._configUrl || null });
    };

    _onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };

    _debug(...args) {
      if (this.hasAttribute('data-debug')) {
        console.log('[product-customizer]', ...args);
      }
    }

    _lockScroll() {
      if (this._lockOwned) return;
      document.documentElement.classList.add(LOCK_CLASS);
      this._lockOwned = true;
    }

    _unlockScroll() {
      if (!this._lockOwned) return;
      document.documentElement.classList.remove(LOCK_CLASS);
      this._lockOwned = false;
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, ProductCustomizerModal);
  }

  window.ProductCustomizer = {
    open() {
      window.dispatchEvent(new CustomEvent(OPEN_EVENT));
    },
    close() {
      window.dispatchEvent(new CustomEvent(CLOSE_EVENT));
    },
  };
})();
